import clsx from 'clsx';
import React, { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from '../Button';
import Icon from '../Icon';

export function Pagination({
  children,
  currentPage,
  long = false,
  pagesCount = 0,
  onPreviousClick,
  onNextClick,
  onPreviousMouseOver,
  onNextMouseOver,
}: {
  children: ReactElement<unknown> | ReactElement<unknown>[];
  pagesCount: number;
  currentPage: number;
  long?: boolean;
  onPreviousClick?: () => void;
  onNextClick?: () => void;
  onPreviousMouseOver?: () => void;
  onNextMouseOver?: () => void;
}) {
  const { t } = useTranslation('ui');
  const visiblePagesCount = long ? 12 : 5;

  const pages = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    // Type guard to ensure we have a valid element with props
    const element = child as React.ReactElement<
      React.HTMLAttributes<HTMLElement>
    >;

    const newClassName = clsx(
      element.props.className,
      'flex size-8 items-center justify-center rounded',
      'typ-button-xs active:bg-blue-10 active:text-blue-100 enabled:hover:bg-neutral-10',
      {
        'bg-blue-110 text-white enabled:hover:bg-blue-110 enabled:hover:text-neutral-110':
          currentPage - 1 === index,
      },
    );

    return (
      <li key={index} className="text-neutral-110">
        {React.cloneElement(element, {
          'aria-current': currentPage - 1 === index ? 'page' : undefined,
          ...(currentPage - 1 === index
            ? { 'data-testid': 'pagination-current-page' }
            : {}),
          className: newClassName,
        })}
      </li>
    );
  });

  if (pagesCount <= 1) return null;

  const visibleOnEachSide = Math.floor(visiblePagesCount / 2);

  const firstVisiblePages = pages.slice(0, visiblePagesCount);
  const showMoreThanFirstPages =
    currentPage > visiblePagesCount - visibleOnEachSide;
  const showEndPages = currentPage > pagesCount - (visibleOnEachSide + 1);

  const [firstPage] = pages;
  const lastPage = pages[pagesCount - 1];

  const showDots = pagesCount > visiblePagesCount;

  const nearestPages = showEndPages
    ? pages.slice(-visiblePagesCount + 1)
    : pages.slice(
        currentPage - visibleOnEachSide,
        currentPage + visibleOnEachSide,
      );

  const dots = (
    <li key="dots" data-testid="dots">
      <div className="text-neutral-110 flex size-8 items-end justify-center pb-0.5">
        <Icon type="more" size="tiny" />
      </div>
    </li>
  );

  const pageLinks =
    pagesCount < visiblePagesCount ? (
      pages
    ) : (
      <>
        {showMoreThanFirstPages
          ? [firstPage, showDots ? dots : null, ...nearestPages].filter(Boolean)
          : firstVisiblePages}
        {showDots && showEndPages === false ? [dots, lastPage] : null}
      </>
    );

  const disablePrevious = currentPage === 1;
  const disableNext = currentPage === pagesCount;

  return (
    <nav role="navigation" aria-label="pagination navigation">
      <ul className="flex select-none">
        <li>
          <BaseButton
            aria-label={t('previousPage')}
            disabled={disablePrevious}
            onClick={disablePrevious ? undefined : onPreviousClick}
            onMouseOver={disablePrevious ? undefined : onPreviousMouseOver}
            className={clsx(
              'typ-button-xs enabled:hover:bg-neutral-10 enabled:active:bg-blue-10 rounded enabled:active:text-blue-100',
              'flex size-8 items-center justify-center',
              disablePrevious ? 'text-neutral-60' : 'text-neutral-110',
            )}
          >
            <Icon type="chevron-left-large" size="small" />
          </BaseButton>
        </li>
        {pageLinks}
        <li>
          <BaseButton
            aria-label={t('nextPage')}
            disabled={disableNext}
            onClick={disableNext ? undefined : onNextClick}
            onMouseOver={disableNext ? undefined : onNextMouseOver}
            className={clsx(
              'typ-button-xs enabled:hover:bg-neutral-10 enabled:active:bg-blue-10 rounded enabled:active:text-blue-100',
              'flex size-8 items-center justify-center',
              disableNext ? 'text-neutral-60' : 'text-neutral-110',
            )}
          >
            <Icon type="chevron-right-large" size="small" />
          </BaseButton>
        </li>
      </ul>
    </nav>
  );
}

export default Pagination;
